/**
 * GIWAHS — Adaptive Journey Bootstrap (AJIP-16.02.002 · WS2)
 *                          + Core Engine surface (AJIP-16.02.003)
 * ---------------------------------------------------------------------
 * Isolated entry point for every Adaptive Journey capability.
 *
 * Contract
 *   • initAdaptiveJourneys() is the single ignition point.
 *   • When JOURNEYS_ENABLED is FALSE (default) the function returns
 *     immediately. Zero API calls. Zero listeners. Zero renders. Zero
 *     background work. Zero measurable cost.
 *   • When JOURNEYS_ENABLED is TRUE the function constructs a single
 *     shared JourneyEngine and registers the canonical initial
 *     journeys. It never mounts UI. It never touches the DOM. It never
 *     makes network calls.
 *   • Every future capability MUST live inside src/adaptiveJourneys/
 *     and MUST NOT be imported from outside this module while its
 *     sub-flag is FALSE.
 *
 * Idempotency
 *   initAdaptiveJourneys() is idempotent. Repeated calls after a
 *   successful initialisation return the same shared engine.
 *
 * Public surface exposed by this module (AJIP-16.02.003)
 *   initAdaptiveJourneys, isAdaptiveJourneysInitialised
 *   getJourneyEngine     — returns the shared engine or null when
 *                          the master flag is disabled.
 *   JourneyEngine        — class, for tests and future extensions.
 *   JourneyRegistry, JourneyContext, evaluateTransition, ...
 */
import { isFeatureEnabled } from '../lib/featureFlags';
import { JourneyEngine } from './core/JourneyEngine';
import { JourneyRegistry } from './core/JourneyRegistry';
import { JourneyContext } from './core/JourneyContext';
import { Observability, OBSERVABILITY_EVENTS } from './core/Observability';
import { STATES, ALL_STATES, TERMINAL_STATES, isValidState, isTerminalState } from './core/JourneyState';
import { LIFECYCLE_EVENTS, ALL_LIFECYCLE_EVENTS } from './core/JourneyLifecycle';
import { evaluateTransition } from './core/TransitionEngine';
import { validateJourneyDefinition, freezeDefinition } from './core/JourneyConfig';
import * as JourneyErrors from './core/JourneyErrors';
import { registerCanonicalJourneys, CANONICAL_JOURNEYS } from './registry/canonicalJourneys';
import { PersistenceService } from './persistence/PersistenceService';
import { RetentionPolicy } from './persistence/RetentionPolicy';
import { SavePolicy } from './persistence/SavePolicy';
import { InMemoryAdapter } from './persistence/adapters/InMemoryAdapter';
import { LocalStorageAdapter } from './persistence/adapters/LocalStorageAdapter';
import { assertAdapter } from './persistence/PersistenceAdapter';
import * as PersistenceErrors from './persistence/PersistenceErrors';
import { PERSISTENCE_EVENTS } from './persistence/PersistenceObservability';
import { RegistrationService } from './registrationDiscovery/RegistrationService';
import { DiscoveryService } from './registrationDiscovery/DiscoveryService';
import { validateExtendedDefinition } from './registrationDiscovery/DefinitionValidator';
import { evaluateEligibility, DECISIONS as ELIGIBILITY_DECISIONS } from './registrationDiscovery/EligibilityEvaluator';
import { orderJourneys } from './registrationDiscovery/DiscoveryOrdering';
import { REGISTRATION_DISCOVERY_EVENTS } from './registrationDiscovery/RegistrationDiscoveryObservability';
import * as RegistrationDiscoveryErrors from './registrationDiscovery/RegistrationDiscoveryErrors';
import { CANONICAL_DEFINITIONS_V2, registerCanonicalV2 } from './registrationDiscovery/canonicalDefinitions';
import { ResolutionService } from './resolutionActivation/ResolutionService';
import { ActivationService } from './resolutionActivation/ActivationService';
import { JourneyCommands } from './resolutionActivation/JourneyCommands';
import { IdempotencyStore } from './resolutionActivation/IdempotencyStore';
import { validateResolutionRequest } from './resolutionActivation/ResolutionRequestValidator';
import { assertActivationAuthorised, GATE_IDS } from './resolutionActivation/ActivationAuthorisationGate';
import {
  RESOLUTION_DECISIONS,
  CONFLICT_STATUSES,
  makeResolutionDecision,
  isResolvedForActivation,
  isResolvedForRestoration,
} from './resolutionActivation/ResolutionDecisionContract';
import {
  ACTIVATION_STATUSES,
  IDEMPOTENCY_STATUSES,
  RESTORATION_STATUSES,
  makeActivationResult,
  buildJourneyInstanceId,
} from './resolutionActivation/ActivationResultContract';
import {
  KNOWN_REQUEST_FIELDS,
  REQUIRED_REQUEST_FIELDS,
  FORBIDDEN_REQUEST_KEYS,
  ENTRY_POINTS,
  MAX_REQUEST_BYTES,
} from './resolutionActivation/ResolutionRequestContract';
import {
  RESOLUTION_ACTIVATION_EVENTS,
  ResolutionActivationObservability,
} from './resolutionActivation/ResolutionActivationObservability';
import * as ResolutionActivationErrors from './resolutionActivation/ResolutionActivationErrors';
import { BuyerJourneyOrchestrator, BUYER_JOURNEY_ID, BUYER_JOURNEY_VERSION, buildBuyerInstanceId } from './buyerJourney/BuyerJourneyOrchestrator';
import { BUYER_STAGES, BUYER_EVENTS, STAGE_TO_ENGINE_STATE } from './buyerJourney/BuyerJourneyModel';
import { BUYER_JOURNEY_EVENTS, BuyerJourneyObservability } from './buyerJourney/BuyerJourneyObservability';
import * as BuyerJourneyErrors from './buyerJourney/BuyerJourneyErrors';
import * as RFQIntegrationBoundary from './buyerJourney/RFQIntegrationBoundary';
import * as TrustMatchBoundary from './buyerJourney/TrustMatchBoundary';
// AJIP-16.02.008 — Supplier Journey (append-only additions).
import {
  SupplierJourneyOrchestrator, SUPPLIER_JOURNEY_ID, SUPPLIER_JOURNEY_VERSION,
  buildSupplierInstanceId,
} from './supplierJourney/SupplierJourneyOrchestrator';
import {
  SUPPLIER_STAGES, SUPPLIER_EVENTS,
  STAGE_TO_ENGINE_STATE as SUPPLIER_STAGE_TO_ENGINE_STATE,
} from './supplierJourney/SupplierJourneyModel';
import {
  SUPPLIER_JOURNEY_EVENTS, SupplierJourneyObservability,
} from './supplierJourney/SupplierJourneyObservability';
import * as SupplierJourneyErrors from './supplierJourney/SupplierJourneyErrors';
import * as SupplierJourneyOwnership from './supplierJourney/SupplierJourneyOwnership';
import * as BuyerSupplierBoundary from './supplierJourney/BuyerSupplierBoundary';
import * as SupplierRFQQuoteBoundary from './supplierJourney/SupplierRFQQuoteBoundary';
import * as SupplierVerificationTrustBoundary from './supplierJourney/SupplierVerificationTrustBoundary';
import * as SupplierMatchOpportunityBoundary from './supplierJourney/SupplierMatchOpportunityBoundary';
// AJIP-16.02.009 — Factory Journey (append-only additions).
import {
  FactoryJourneyOrchestrator, FACTORY_JOURNEY_ID, FACTORY_JOURNEY_VERSION,
  buildFactoryInstanceId,
} from './factoryJourney/FactoryJourneyOrchestrator';
import {
  FACTORY_STAGES, FACTORY_COMMANDS, FACTORY_STATUSES,
  STAGE_TO_ENGINE_STATE as FACTORY_STAGE_TO_ENGINE_STATE,
  STATUS_TO_ENGINE_STATE as FACTORY_STATUS_TO_ENGINE_STATE,
} from './factoryJourney/FactoryJourneyModel';
import {
  FACTORY_JOURNEY_EVENTS, FactoryJourneyObservability,
} from './factoryJourney/FactoryJourneyObservability';
import * as FactoryJourneyErrors from './factoryJourney/FactoryJourneyErrors';
import * as FactoryJourneyOwnership from './factoryJourney/FactoryJourneyOwnership';
import * as BuyerFactoryBoundary from './factoryJourney/BuyerFactoryBoundary';
import * as SupplierFactoryBoundary from './factoryJourney/SupplierFactoryBoundary';

let _initialised = false;
let _engine = null;
let _persistence = null;
let _registrationService = null;
let _discoveryService = null;
let _resolutionService = null;
let _activationService = null;
let _journeyCommands = null;
let _buyerJourneyOrchestrator = null;
let _supplierJourneyOrchestrator = null;
let _factoryJourneyOrchestrator = null;

const initAdaptiveJourneys = () => {
  if (_initialised) return {
    ok: true, active: true,
    engine: _engine, persistence: _persistence,
    registrationService: _registrationService, discoveryService: _discoveryService,
    resolutionService: _resolutionService, activationService: _activationService,
    journeyCommands: _journeyCommands,
  };
  if (!isFeatureEnabled('JOURNEYS_ENABLED')) {
    return { ok: true, active: false, reason: 'flag_disabled' };
  }
  _engine = new JourneyEngine();
  // Registration & Discovery — the canonical source of truth per
  // Founder Ruling RD-02. RegistrationService.register() writes into
  // the protected engine registry via toBaseDefinition(), so the
  // engine ends up with the same six canonical journeys without
  // requiring a separate legacy registration call.
  _registrationService = new RegistrationService({ registry: _engine.registry });
  registerCanonicalV2(_registrationService);
  _discoveryService = new DiscoveryService({ registrationService: _registrationService });
  if (isFeatureEnabled('JOURNEYS_PERSISTENCE')) {
    // Adapter is selected at initialisation. Framework never asks where
    // persistence lives beyond the contract. LocalStorageAdapter is the
    // browser default; InMemoryAdapter is used when localStorage is
    // unavailable (SSR / degraded environments).
    let adapter;
    try {
      adapter = new LocalStorageAdapter();
    } catch (_e) {
      adapter = new InMemoryAdapter();
    }
    _persistence = new PersistenceService({
      adapter,
      retentionPolicy: new RetentionPolicy(),
      savePolicy: new SavePolicy(),
    });
    try {
      _persistence.attachToEngine(_engine, { principalRef: null });
    } catch (_e) {
      // Attachment must never block engine construction. Fail-closed.
      _persistence = null;
    }
  }
  // AJIP-16.02.006 — Resolution & Activation Framework
  // Consumes published interfaces of Registration, Discovery, Engine
  // and Persistence. Never modifies any protected component.
  _resolutionService = new ResolutionService({
    registrationService: _registrationService,
    discoveryService: _discoveryService,
    engine: _engine,
    persistenceService: _persistence,
    idempotencyStore: null,
  });
  const _idempotency = new IdempotencyStore();
  _activationService = new ActivationService({
    engine: _engine,
    resolutionService: _resolutionService,
    persistenceService: _persistence,
    idempotencyStore: _idempotency,
    observability: _resolutionService.observability,
  });
  _journeyCommands = new JourneyCommands({
    engine: _engine,
    observability: _resolutionService.observability,
  });
  // AJIP-16.02.007 — Buyer Journey Orchestrator (sole composition layer).
  _buyerJourneyOrchestrator = new BuyerJourneyOrchestrator({
    engine: _engine,
    registrationService: _registrationService,
    discoveryService: _discoveryService,
    resolutionService: _resolutionService,
    activationService: _activationService,
    journeyCommands: _journeyCommands,
    persistenceService: _persistence,
  });
  try { _buyerJourneyOrchestrator.register(); } catch (_e) { _buyerJourneyOrchestrator = null; }
  // AJIP-16.02.008 — Supplier Journey Orchestrator (sole composition layer).
  _supplierJourneyOrchestrator = new SupplierJourneyOrchestrator({
    engine: _engine,
    registrationService: _registrationService,
    discoveryService: _discoveryService,
    resolutionService: _resolutionService,
    activationService: _activationService,
    journeyCommands: _journeyCommands,
    persistenceService: _persistence,
  });
  try { _supplierJourneyOrchestrator.register(); } catch (_e) { _supplierJourneyOrchestrator = null; }
  // AJIP-16.02.009 — Factory Journey Orchestrator (sole composition layer).
  _factoryJourneyOrchestrator = new FactoryJourneyOrchestrator({
    engine: _engine,
    registrationService: _registrationService,
    discoveryService: _discoveryService,
    resolutionService: _resolutionService,
    activationService: _activationService,
    journeyCommands: _journeyCommands,
    persistenceService: _persistence,
  });
  try { _factoryJourneyOrchestrator.register(); } catch (_e) { _factoryJourneyOrchestrator = null; }
  _initialised = true;
  return {
    ok: true, active: true,
    engine: _engine, persistence: _persistence,
    registrationService: _registrationService, discoveryService: _discoveryService,
    resolutionService: _resolutionService, activationService: _activationService,
    journeyCommands: _journeyCommands,
  };
};

const isAdaptiveJourneysInitialised = () => _initialised;

const getJourneyEngine = () => _engine;
const getPersistenceService = () => _persistence;
const getRegistrationService = () => _registrationService;
const getDiscoveryService = () => _discoveryService;
const getResolutionService = () => _resolutionService;
const getActivationService = () => _activationService;
const getJourneyCommands = () => _journeyCommands;
const getBuyerJourneyOrchestrator = () => _buyerJourneyOrchestrator;
const getSupplierJourneyOrchestrator = () => _supplierJourneyOrchestrator;
const getFactoryJourneyOrchestrator = () => _factoryJourneyOrchestrator;

export {
  initAdaptiveJourneys,
  isAdaptiveJourneysInitialised,
  getJourneyEngine,
  getPersistenceService,
  getRegistrationService,
  getDiscoveryService,
  getResolutionService,
  getActivationService,
  getJourneyCommands,
  getBuyerJourneyOrchestrator,
  getSupplierJourneyOrchestrator,
  getFactoryJourneyOrchestrator,
  JourneyEngine,
  JourneyRegistry,
  JourneyContext,
  Observability,
  OBSERVABILITY_EVENTS,
  STATES,
  ALL_STATES,
  TERMINAL_STATES,
  isValidState,
  isTerminalState,
  LIFECYCLE_EVENTS,
  ALL_LIFECYCLE_EVENTS,
  evaluateTransition,
  validateJourneyDefinition,
  freezeDefinition,
  registerCanonicalJourneys,
  CANONICAL_JOURNEYS,
  JourneyErrors,
  PersistenceService,
  RetentionPolicy,
  SavePolicy,
  InMemoryAdapter,
  LocalStorageAdapter,
  assertAdapter,
  PersistenceErrors,
  PERSISTENCE_EVENTS,
  RegistrationService,
  DiscoveryService,
  validateExtendedDefinition,
  evaluateEligibility,
  ELIGIBILITY_DECISIONS,
  orderJourneys,
  REGISTRATION_DISCOVERY_EVENTS,
  RegistrationDiscoveryErrors,
  CANONICAL_DEFINITIONS_V2,
  registerCanonicalV2,
  // AJIP-16.02.006 — Resolution & Activation Framework exports
  ResolutionService,
  ActivationService,
  JourneyCommands,
  IdempotencyStore,
  validateResolutionRequest,
  assertActivationAuthorised,
  GATE_IDS,
  RESOLUTION_DECISIONS,
  CONFLICT_STATUSES,
  makeResolutionDecision,
  isResolvedForActivation,
  isResolvedForRestoration,
  ACTIVATION_STATUSES,
  IDEMPOTENCY_STATUSES,
  RESTORATION_STATUSES,
  makeActivationResult,
  buildJourneyInstanceId,
  KNOWN_REQUEST_FIELDS,
  REQUIRED_REQUEST_FIELDS,
  FORBIDDEN_REQUEST_KEYS,
  ENTRY_POINTS,
  MAX_REQUEST_BYTES,
  RESOLUTION_ACTIVATION_EVENTS,
  ResolutionActivationObservability,
  ResolutionActivationErrors,
  // AJIP-16.02.007 — Buyer Journey
  BuyerJourneyOrchestrator, BUYER_JOURNEY_ID, BUYER_JOURNEY_VERSION,
  buildBuyerInstanceId, BUYER_STAGES, BUYER_EVENTS, STAGE_TO_ENGINE_STATE,
  BUYER_JOURNEY_EVENTS, BuyerJourneyObservability,
  BuyerJourneyErrors, RFQIntegrationBoundary, TrustMatchBoundary,
  // AJIP-16.02.008 — Supplier Journey
  SupplierJourneyOrchestrator, SUPPLIER_JOURNEY_ID, SUPPLIER_JOURNEY_VERSION,
  buildSupplierInstanceId,
  SUPPLIER_STAGES, SUPPLIER_EVENTS, SUPPLIER_STAGE_TO_ENGINE_STATE,
  SUPPLIER_JOURNEY_EVENTS, SupplierJourneyObservability,
  SupplierJourneyErrors, SupplierJourneyOwnership,
  BuyerSupplierBoundary, SupplierRFQQuoteBoundary,
  SupplierVerificationTrustBoundary, SupplierMatchOpportunityBoundary,
  // AJIP-16.02.009 — Factory Journey
  FactoryJourneyOrchestrator, FACTORY_JOURNEY_ID, FACTORY_JOURNEY_VERSION,
  buildFactoryInstanceId,
  FACTORY_STAGES, FACTORY_COMMANDS, FACTORY_STATUSES,
  FACTORY_STAGE_TO_ENGINE_STATE, FACTORY_STATUS_TO_ENGINE_STATE,
  FACTORY_JOURNEY_EVENTS, FactoryJourneyObservability,
  FactoryJourneyErrors, FactoryJourneyOwnership,
  BuyerFactoryBoundary, SupplierFactoryBoundary,
};
export default {
  initAdaptiveJourneys, isAdaptiveJourneysInitialised,
  getJourneyEngine, getPersistenceService,
  getRegistrationService, getDiscoveryService,
  getResolutionService, getActivationService, getJourneyCommands,
  getBuyerJourneyOrchestrator, getSupplierJourneyOrchestrator,
  getFactoryJourneyOrchestrator,
};
